import React, { useState } from "react";
import { useMutation, useQuery, gql } from "@apollo/client";
import { toast } from "react-toastify";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useTrackedState } from '@/utils/useTrackedState';
import { CheckCircle, XCircle , Ban} from "lucide-react";
import { useProgramColor } from '@/store/useProgramColor'; 
import { isoDateToMDY } from "@/utils/common";

interface Program {
  programDescription: string;
  entryDate: string;
  exitDate: string;
  provider: string;
  disposition: string;
}

interface NameHistory {
  entrydate: string;
  firstName: string;
  lastName: string;
  program: string;
}

interface DispositionHistory {
  dispositionDate: string;
  disposition: string;
  program: string;
}

interface Scan {
  filedate: string;
  filetype: string;
  filelabel: string;
  filename: string;
}

interface Participant {
  activeProgram?: {
    programDescription: string;
  };
  active?: boolean;
  programs?: string[];
  mrun?: string;
  champid?: string;
  disposition?: string;
  providerHistory?: Array<{ value: string }>;
  genderHistory?: Array<{ value: string }>;
  releaseDate?: string;
  pathwayHistory?: Array<{ value: string }>;
  nameHistory?: NameHistory[];
  dispositionHistory?: DispositionHistory[];
  programDetails?: Program[];
  scans?: Scan[];
  name?: any;
  probation?: string;
  cii?: string;
  ssn?: string;
  isNumber?: string;
  booking?: string;
  maxDate?: string;
  completionDate?: string;
  dischargeDate?: string;
  commitmentDate?: string;
  jcit?: string;
  diversionDate?: string;
  notes?: string;
  incidents?: string;
  dob?: string;
  adherence?: string;
  champId?: string;
  fin?: string;
  cin?: string;
  healthplan?: {
    healthplan?: string;
    entrydate?: string;
  };
  arrestDate?: string;
  rso?:string,
  cases?: string;
}

interface EditParticipantProps {
  refetch: () => void;
  isOpen: boolean;
  onClose: () => void;
  program: any;
  odrnumber: number;
  data?: {
    getParticipantByODRNumber: Participant;
  };
  programOnly?: boolean;
  isActive: boolean;
}

interface FormState {
  mrun: string;
  champid: string;
  disposition: string;
  provider: string;
  gender: string;
  active: boolean;
  releaseDate: string;
  pathway: string;
  program: string;
  names: NameHistory[];
  isOpen: boolean;
  race: string;
  dob: string;
  isReal: boolean;
  fivePercent: boolean;
  isNumbers: string[];
  ab109Funded: string;
  dispositionDate: string;
  programTransfers: string[];
  name: any;
  probation: string;
  adherence: string;
  champId: string;
  fin: string;
  cin: string;
  healthplan: string;
  arrestDate: string;
  rso:string;
  cases: string;
  medicalProblems: string;
  psychotropicMeds: string;
  otherMeds: string;
  clinicalPresentation: string;
  currentMeds: string;
  substanceHistory: string;
  istStatus: string;
  ist: string;
  fso: string;
  booking: string;
  bookingNumber: string;
  maxDate: string;
  completionDate: string;
  isNumber: string;
  dischargeDate: string;
  commitmentDate: string;  
  jcit: string;
  diversionDate: string;
  notes: string;
  incidents: string;
  firstName: string;
  lastName: string;
  cii: string;
  ssn: string;
  mrunNumber: string;
  mrunStatus: 'open' | 'closed';
}

const normalizeDateInputValue = (value?: string) => {
  if (!value) {
    return "";
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.includes(" ")) {
    const [datePart] = trimmed.split(" ");
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }
  }

  if (trimmed.includes("T")) {
    const isoDate = new Date(trimmed);
    if (!Number.isNaN(isoDate.getTime())) {
      return isoDate.toISOString().slice(0, 10);
    }
  }

  const parts = trimmed.split("/");
  if (parts.length !== 3) {
    return trimmed;
  }

  const [month, day, year] = parts.map((part) => part.trim());
  if (!month || !day || !year) {
    return "";
  }

  let normalizedYear = year;
  if (year.length === 2) {
    normalizedYear = `20${year}`;
  }

  const normalizedMonth = month.padStart(2, "0");
  const normalizedDay = day.padStart(2, "0");
  return `${normalizedYear}-${normalizedMonth}-${normalizedDay}`;
};

const GET_HEALTHPLAN_PROVIDERS = gql`
  query GetHealthPlanProviders {
    getHealthPlanProviders {
      providerId
      providerName
    }
  }
`;

const UPDATE_PARTICIPANT = gql`
  mutation UpdateParticipantByODRNumber($odrnumber: Int!, $participant: Int, $updates: UpdateParticipantInput!) {
    updateParticipantByODRNumber(odrnumber: $odrnumber, participant: $participant, updates: $updates) {
      message
    }
  }
`;

export const EditParticipant: React.FC<EditParticipantProps> = ({ isOpen, onClose, program, odrnumber, data, programOnly, isActive , refetch} : EditParticipantProps ) => {
  const programNameDescription = data?.getParticipantByODRNumber?.activeProgram?.programDescription
  const participant = data?.getParticipantByODRNumber;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { programColor } = useProgramColor();
  const { data: healthPlansData, loading: healthPlansLoading } = useQuery(GET_HEALTHPLAN_PROVIDERS);
  
  
  const [activeProgram, setActiveProgram] = useState<string>(
    participant?.activeProgram?.programDescription || ""
  );

  const [formState, handleInputChange, getModifiedValues] = useTrackedState<FormState>({
    firstName: participant?.name?.firstName || "",
    lastName: participant?.name?.lastName || "",
    program: participant?.activeProgram?.programDescription || "",
    names: participant?.nameHistory || [],
    isOpen: false,
    isReal: false,
    champid: participant?.champid || "",
    gender: participant?.genderHistory?.[0]?.value || "",
    disposition: participant?.disposition || "",
    releaseDate: participant?.releaseDate || "",
    pathway: participant?.pathwayHistory?.[0]?.value || "",
    provider: participant?.providerHistory?.[0]?.value || "",
    mrun: participant?.mrun || "",
    adherence: participant?.adherence || "",
    champId: participant?.champId || "",
    fin: participant?.fin || "",
    cin: participant?.cin || "",
    healthplan: participant?.healthplan?.healthplan || "",
    arrestDate: participant?.arrestDate || "", 
    name: "",
    rso: participant?.rso || '0', 
    probation: participant?.probation || "",
    cases: participant?.cases || "",
    medicalProblems: "",
    psychotropicMeds: "",
    otherMeds: "",
    clinicalPresentation: "",
    currentMeds: "",
    substanceHistory: "",
    istStatus: "",
    ist: "",
    fso: "",
    booking: participant?.booking || "",
    bookingNumber: "",
    maxDate: "",
    dob: normalizeDateInputValue(participant?.dob),
    completionDate: "",    
    dischargeDate: "",
    commitmentDate: "",
    jcit: "",
    diversionDate: participant?.diversionDate || "",
    notes: "",
    incidents: "",
    fivePercent: false,
    isNumbers: [],
    ab109Funded: "",
    dispositionDate: "",
    programTransfers: [],    
    isNumber: participant?.isNumber || "",
    race: "",
    cii: participant?.cii || "",
    ssn: participant?.ssn || "",
    mrunNumber: participant?.mrun ? participant.mrun.split('-')[0] : '',
    mrunStatus: participant?.mrun?.includes('open') ? 'open' : 'closed',
    active: isActive || false, 
  });

  const [updateParticipant] = useMutation(UPDATE_PARTICIPANT, {
    onCompleted: () => {
      toast.success("Participant updated successfully");
      onClose();
    },
    onError: (error) => {
      toast.error(`Error updating participant: ${error.message}`);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const modifiedValues = getModifiedValues();
      if (Object.keys(modifiedValues).length === 0) {
        toast.info("No changes to save");
        return;
      }

      // Check if any name-related fields were modified
      const nameFieldsModified = ['firstName', 'lastName', 'isReal'].some(
        field => field in modifiedValues
      );

      // Create the final updates object
      const updates: Record<string, any> = { ...modifiedValues };

      if (Object.prototype.hasOwnProperty.call(modifiedValues, 'healthplan')) {
        updates.healthplan = { healthplan: formState.healthplan };
      }

      if (modifiedValues.mrun) {
        updates.mrun = formState.mrunNumber + '-' + formState.mrunStatus
        delete updates.mrunNumber;
        delete updates.mrunStatus;
      }
      
      // If any name field was modified, group them together
      if (nameFieldsModified) {
        // Remove individual name fields
        delete updates.firstName;
        delete updates.lastName;
        delete updates.isReal;                
        // Add grouped name object with correct field names
        updates.name = {
          firstName: formState.firstName,
          lastName: formState.lastName,
          isName: Boolean(formState.isReal),
          isAlias: !formState.isReal
        };
      }

      await updateParticipant({
        variables: {
          odrnumber: odrnumber,
          //participant: program.programNumber,
          updates: updates,
        },
      });
      refetch();
    } catch (error) {
      console.error("Submit error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMrunChange = (type: 'number' | 'status', value: string) => {
    if (type === 'number') {
      const sanitizedValue = value.replace(/\D/g, '').slice(0, 9);
      handleInputChange('mrunNumber', sanitizedValue);
    } else {
      handleInputChange('mrunStatus', value as 'open' | 'closed');
    }
    
    const number = type === 'number' ? value : formState.mrunNumber;
    const status = type === 'status' ? value : formState.mrunStatus;
    if (number) {
      handleInputChange('mrun', `${number}-${status}`);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="min-w-[80vw] max-w-[90vw] max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader className="mb-4">
          <DialogTitle className="text-2xl font-bold">Edit Participant Details</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="border-0 shadow-none">
            <CardHeader className="px-0 pt-0 pb-2">
              <CardTitle className="text-xl font-semibold text-blue-800">
                {programOnly==true ? `${program.programDescription?.toUpperCase()} - ${program.programParticipantNumber}` : ''}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0">
              { programOnly!=true && <div className="space-y-6">
                
                {/* Personal Info Section */}
                <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Personal Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="firstName" className="text-xs font-semibold text-gray-600">First Name</Label>
                      <Input
                        id="firstName"
                        value={formState.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className="h-8 text-sm bg-white"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="lastName" className="text-xs font-semibold text-gray-600">Last Name</Label>
                      <Input
                        id="lastName"
                        value={formState.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className="h-8 text-sm bg-white"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-semibold text-gray-600">Name Type</Label>
                      <RadioGroup
                        value={formState.isReal ? "realname" : "alias"}
                        onValueChange={(value) => {
                          handleInputChange('isReal', value === "realname" ? true : false);
                        }}
                        className="flex gap-3 h-8 items-center"
                      >
                        <div className="flex items-center space-x-1.5">
                          <RadioGroupItem value="realname" id="realname" className="h-3.5 w-3.5" />
                          <Label htmlFor="realname" className="font-normal text-sm cursor-pointer">Real</Label>
                        </div>
                        <div className="flex items-center space-x-1.5">
                          <RadioGroupItem value="alias" id="alias" className="h-3.5 w-3.5" />
                          <Label htmlFor="alias" className="font-normal text-sm cursor-pointer">Alias</Label>
                        </div>
                      </RadioGroup>
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="dob" className="text-xs font-semibold text-gray-600">Date of Birth</Label>
                        <Input
                            id="dob"
                            type="date"
                            value={formState.dob}
                            onChange={(e) => handleInputChange('dob', e.target.value)}
                            className="h-8 text-sm bg-white"
                        />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="race" className="text-xs font-semibold text-gray-600">Race</Label>
                      <Select
                        value={formState.race}
                        onValueChange={(value) => handleInputChange('race', value)}
                      >
                        <SelectTrigger className="h-8 text-sm bg-white">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="American Indian">American Indian/Indigenous</SelectItem>
                          <SelectItem value="Asian">Asian</SelectItem>
                          <SelectItem value="Black">Black/African American</SelectItem>
                          <SelectItem value="Hispanic">Hispanic/Latinx</SelectItem>
                          <SelectItem value="Middle Eastern">Middle Eastern</SelectItem>
                          <SelectItem value="Pacific Islander">Pacific Islander</SelectItem>
                          <SelectItem value="White">White</SelectItem>
                          <SelectItem value="Doesn't Know">Doesn't Know</SelectItem>
                          <SelectItem value="Refused">Refused</SelectItem>
                          <SelectItem value="Unknown">Unknown</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                     <div className="space-y-1 md:col-span-3">
                      <Label className="text-xs font-semibold text-gray-600">Gender</Label>
                      <RadioGroup
                        value={formState.gender}
                        onValueChange={(value: string) => handleInputChange('gender', value)}
                        className="flex gap-4 h-8 items-center"
                      >
                        {['Male', 'Female', 'Transgender'].map(g => (
                          <div key={g} className="flex items-center space-x-1.5">
                            <RadioGroupItem value={g} id={g} className="h-3.5 w-3.5" />
                            <Label htmlFor={g} className="font-normal text-sm cursor-pointer">{g}</Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  </div>
                </div>

                {/* Identification Section */}
                <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Identification</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-3">
                    <div className="space-y-1 col-span-1 md:col-span-2 lg:col-span-1">
                      <Label htmlFor="mrun" className="text-xs font-semibold text-gray-600">MRUN</Label>
                      <div className="flex gap-1">
                        <Input
                          id="mrunNumber"
                          value={formState.mrunNumber}
                          onChange={(e) => handleMrunChange('number', e.target.value)}
                          maxLength={9}
                          placeholder="9-digit"
                          className="flex-1 h-8 text-sm bg-white"
                        />
                        <Select
                          value={formState.mrunStatus}
                          onValueChange={(value) => handleMrunChange('status', value)}
                        >
                          <SelectTrigger className="w-[75px] h-8 text-sm bg-white px-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="closed">Close</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="ssn" className="text-xs font-semibold text-gray-600">SSN</Label>
                      <Input
                        id="ssn"
                        value={formState.ssn}
                        onChange={(e) => handleInputChange('ssn', e.target.value)}
                        className="h-8 text-sm bg-white"
                        maxLength={11}
                        placeholder="XXX-XX-XXXX"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label htmlFor="champid" className="text-xs font-semibold text-gray-600">CHAMP ID</Label>
                      <Input
                        id="champid"
                        value={formState.champid}
                        onChange={(e) => handleInputChange('champid', e.target.value)}
                        className="h-8 text-sm bg-white"
                        maxLength={6}
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="isNumber" className="text-xs font-semibold text-gray-600">IS Number</Label>
                      <Input
                        id="isNumber"
                        value={formState.isNumber}
                        onChange={(e) => handleInputChange('isNumber', e.target.value)}
                        className="h-8 text-sm bg-white"
                        maxLength={15}
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="cii" className="text-xs font-semibold text-gray-600">CII</Label>
                      <Input
                        id="cii"
                        value={formState.cii}
                        onChange={(e) => handleInputChange('cii', e.target.value)}
                        className="h-8 text-sm bg-white"
                        maxLength={15}
                      />
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="cin" className="text-xs font-semibold text-gray-600">CIN</Label>
                      <Input
                        id="cin"
                        value={formState.cin}
                        onChange={(e) => handleInputChange('cin', e.target.value)}
                        className="h-8 text-sm bg-white"
                        maxLength={15}
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label htmlFor="fin" className="text-xs font-semibold text-gray-600">FIN</Label>
                      <Input
                        id="fin"
                        value={formState.fin}
                        onChange={(e) => handleInputChange('fin', e.target.value)}
                        className="h-8 text-sm bg-white"
                        maxLength={15}
                      />
                    </div>
                  </div>
                </div>

                {/* Status Info */}
                <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Status & Health</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-3">
                    <div className="space-y-1 md:col-span-2">
                      <Label htmlFor="healthplan" className="text-xs font-semibold text-gray-600">Health Plan Provider</Label>
                      <Select
                        value={formState.healthplan}
                        onValueChange={(value) => {
                          handleInputChange('healthplan', value);
                        }}
                      >
                        <SelectTrigger className="h-8 text-sm bg-white">
                          <SelectValue placeholder="Select Plan" />
                        </SelectTrigger>
                        <SelectContent>
                          {healthPlansLoading ? (
                            <SelectItem value="loading" disabled>Loading...</SelectItem>
                          ) : (
                            healthPlansData?.getHealthPlanProviders?.map((hp: any) => (
                              <SelectItem key={hp.providerName} value={hp.providerName}>
                                {hp.providerName}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      {participant?.healthplan?.entrydate && (
                        <p className="text-xs text-gray-500">
                          Selected: {isoDateToMDY(participant.healthplan.entrydate)}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="probation" className="text-xs font-semibold text-gray-600">Probation</Label>
                      <Select
                        value={formState.probation}
                        onValueChange={(value) => handleInputChange('probation', value)}
                      >
                        <SelectTrigger className="h-8 text-sm bg-white">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Yes">Yes</SelectItem>
                          <SelectItem value="No">No</SelectItem>
                          <SelectItem value="Terminated">Terminated</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="rso" className="text-xs font-semibold text-gray-600">RSO</Label>
                      <Select
                        value={formState.rso == '1' ? "Yes" : "No"}
                        onValueChange={(e) => handleInputChange('rso', e)}
                      >
                        <SelectTrigger className="h-8 text-sm bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={'1'}>Yes</SelectItem>
                          <SelectItem value={'0'}>No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>}
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3 pt-2 border-t mt-0">
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="h-8 text-sm bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
            <Button 
              type="button" 
              variant="ghost"
              onClick={onClose}
              className="h-8 text-sm"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditParticipant;
